import * as THREE from "three";
import { freshDiscovery } from "./discovery-state.js";
import { B } from "./blocks.js";
import { amount, addItem, assignSlot, ITEMS } from "./crafting.js";
import { arrowHits, BOW } from "./expansion.js";
import { GIANT } from "./mobs.js";
import { giantRegion, GIANT_SPOT, REGION, regionKey } from "./discovery-terrain.js";
import {
  cellKey,
  deviceState,
  PUZZLES,
  transferChest,
  ensureExpansion,
} from "./expansion-rules.js";
export function expansionTests(T, game) {
  T.playExpansion = async () => {
    const previous = JSON.parse(JSON.stringify(game.save.data));
    const report = { name: "Ember expansion", pass: [], fail: [], errors: [] },
      ok = (x, m) => (x ? report.pass : report.fail).push(m);
    game.testHold = true;
    game.showMenu();
    game.save.data.discovery = freshDiscovery();
    await game.startExplore();
    const E = game.explore,
      X = E.expansion,
      d = E.data,
      s = game.save.data;
    const tp = (x, y, z) => {
      game.resume();
      E.player.spawn(new THREE.Vector3(x, y, z));
      E.stream.update(x, z, true);
    };
    const place = (id, x, y, z) => {
      game.resume();
      tp(x + 0.5, y, z - 2);
      addItem(d, id, 4);
      assignSlot(d, id, 7);
      E.selectSlot(7);
      const r = E.placeBlock([x, y, z]);
      ok(r, "place " + id);
      return [x, y, z, ITEMS[id].block];
    };
    try {
      ok(
        !!X && d.expansion.v === 1,
        "old/fresh profiles initialise the expansion",
      );
      X.questPanel();
      ok(
        game.paused && document.querySelector(".quest-hero"),
        "one readable quest pauses the world",
      );
      X.action("readQuest");
      X.close();
      const chest = place("chest", 20, 41, 4),
        key = cellKey(chest);
      addItem(d, "crystal", 5);
      X.device(key);
      X.action("stash", { id: "crystal", out: "0" });
      ok(
        d.expansion.devices[key].stash.crystal === 1 &&
          amount(d, "crystal") === 4,
        "chest stores exact quantity",
      );
      X.close();
      ok(!X.beforeRemove(chest), "filled chest cannot be destroyed");
      X.device(key);
      X.action("stash", { id: "crystal", out: "1" });
      X.close();
      ok(
        amount(d, "crystal") === 5 && X.beforeRemove(chest),
        "withdraw restores material and chest can be removed",
      );
      ok(
        !transferChest(d, key, "pick_wood", 1),
        "owned equipment cannot be duplicated through storage",
      );
      const furnace = place("furnace", 22, 41, 4);
      addItem(d, "ore", 2);
      addItem(d, "coal", 1);
      const iron = amount(d, "iron");
      X.device(cellKey(furnace));
      X.action("smelt", { id: "iron" });
      ok(
        amount(d, "iron") === iron + 2 && !amount(d, "ore"),
        "furnace turns ore and coal into iron",
      );
      X.close();
      const sign = place("sign_board", 24, 41, 4);
      X.device(cellKey(sign));
      document.querySelector("#sign-text").value = "<b>Sai’s Castle</b>";
      X.action("sign");
      ok(
        d.expansion.devices[cellKey(sign)].text === "<b>Sai’s Castle</b>",
        "sign keeps user text as text",
      );
      const bed = place("bed", 26, 41, 4);
      X.device(cellKey(bed));
      ok(!!d.expansion.home, "bed sets a home point");
      E.playerDied();
      ok(
        E.player.pos.distanceTo(new THREE.Vector3(...d.expansion.home)) < 0.1,
        "empty hearts return to bed",
      );
      E.goHome();
      ok(E.player.pos.z < 0, "Town still returns to village");
      const home = d.expansion.home.slice();
      E.world.set(
        Math.floor(home[0]),
        Math.floor(home[1]),
        Math.floor(home[2]),
        B.STONE,
      );
      // Save the obstruction so a stream reload sees the same occupied respawn space.
      d.blocks.push([
        Math.floor(home[0]),
        Math.floor(home[1]),
        Math.floor(home[2]),
        B.STONE,
      ]);
      E.playerDied();
      ok(
        E.player.pos.z < 0 && !d.expansion.home,
        "blocked bed respawns safely in town",
      );
      d.blocks.pop();
      E.world.set(
        Math.floor(home[0]),
        Math.floor(home[1]),
        Math.floor(home[2]),
        B.AIR,
      );

      const door = place("door", 20, 41, 8);
      ok(E.world.get(20, 42, 8) === B.DOOR, "door has two panels");
      X.device(cellKey(door));
      ok(
        E.world.get(20, 41, 8) === B.DOOR_OPEN &&
          E.world.get(20, 42, 8) === B.DOOR_OPEN,
        "both door panels open together",
      );
      X.device(cellKey(door));
      ok(E.world.get(20, 41, 8) === B.DOOR, "door closes again");
      X.device(cellKey(door));
      tp(20.5, 41, 8.5);
      X.device(cellKey(door));
      ok(
        E.world.get(20, 41, 8) === B.DOOR_OPEN,
        "closing door waits while player is in doorway",
      );
      tp(20.5, 41, 6);
      X.syncDevices();
      ok(
        E.world.get(20, 41, 8) === B.DOOR,
        "door closes when doorway is clear",
      );
      const sw = place("switch", 22, 41, 8);
      place("wire", 23, 41, 8);
      const lamp = place("circuit_lamp", 24, 41, 8);
      tp(22.5, 41, 7);
      X.device(cellKey(sw));
      ok(
        X.power.has(cellKey(lamp)),
        "switch powers lamp through adjacent wire",
      );
      X.device(cellKey(sw));
      ok(!X.power.has(cellKey(lamp)), "switch turns connected circuit off");
      const farm = place("farm_plot", 20, 41, 13);
      addItem(d, "seeds", 1);
      addItem(d, "watering_can", 1);
      X.device(cellKey(farm));
      ok(deviceState(d, farm).planted !== null, "planting consumes one seed");
      X.device(cellKey(farm));
      ok(deviceState(d, farm).water, "watering starts crop timer");
      d.worldTime += 46;
      X.device(cellKey(farm));
      ok(
        amount(d, "wheat") === 3 && d.expansion.harvests === 1,
        "mature crop harvest gives wheat and seeds",
      );
      addItem(d, "lead", 1);
      assignSlot(d, "lead", 7);
      E.selectSlot(7);
      const wild = E.wildlife.animals.get("0,-1:0");
      tp(...wild.obj.position.toArray());
      X.adopt(wild);
      ok(
        d.expansion.animals.length === 1 && !wild.obj.visible,
        "lead adopts sheep safely",
      );
      addItem(d, "egg", 1);
      assignSlot(d, "egg", 7);
      E.selectSlot(7);
      X.useHeld();
      ok(
        d.expansion.animals.some((a) => a.kind === "chicken" && a.baby > 0),
        "egg hatches a growing chick",
      );
      const sheep = d.expansion.animals[0];
      d.expansion.animals.push({
        ...sheep,
        id: 999,
        pos: [...sheep.pos],
        fed: false,
      });
      tp(...sheep.pos);
      X.farmAction();
      X.action("feed");
      X.panel = { type: "animal", id: 999 };
      game.paused = true;
      X.action("feed");
      ok(
        d.expansion.animals.filter((a) => a.kind === "sheep").length === 3,
        "two fed nearby adults produce a baby",
      );
      const rail = place("rail", 20, 41, 16);
      place("rail", 21, 41, 16);
      place("powered_rail", 22, 41, 16);
      addItem(d, "minecart", 1);
      tp(20.5, 41, 15);
      X.startCart(rail);
      const x = E.player.pos.x;
      for (let i = 0; i < 20; i++) X.drive(1 / 60, { moveX: 0, moveY: 0 });
      ok(!!X.rail && E.player.pos.x > x, "minecart travels along player rails");
      X.exitCart();
      ok(!X.rail && !X.cart.visible, "leave cart gives control back");
      tp(38, 41, 25);
      ok(
        E.world.get(37, 40, 25) === B.STONE_BRICK &&
          E.world.get(55, 23, 25) === B.AIR,
        "mine has a descending entrance",
      );
      tp(37.5, 41, 25.5);
      game.camRig.yaw = 0;
      for (let n = 0; n < 600 && E.player.pos.x < 56.5; n++)
        T.step(1, { moveX: -1 });
      T.step(50);
      ok(
        E.player.pos.x > 56 && E.player.pos.y < 24,
        "player can walk down the mine staircase",
      );
      for (let n = 0; n < 1500 && E.player.pos.x > 37.5; n++)
        T.step(1, { moveX: 1 });
      T.step(50);
      ok(
        E.player.pos.x < 38 && E.player.pos.y >= 40,
        "auto-jump climbs all the way out of the mine",
      );
      tp(59.5, 22, 25.5);
      X.marker("vault");
      ok(
        game.paused && document.querySelector(".puzzle-choices"),
        "cave clue pauses gameplay",
      );
      const before = s.gems;
      X.answer("7");
      ok(!d.expansion.flags.cave, "wrong answer supports retry");
      X.answer("12");
      ok(
        d.expansion.flags.cave && s.gems === before + 1,
        "correct cave clue grants treasure once",
      );
      X.answer("12");
      ok(s.gems === before + 1, "repeated answer cannot duplicate rewards");
      X.close();
      place("perch", 20, 41, 20);
      d.dragonHatched = true;
      addItem(d, "saddle", 1);
      X.puzzle("flight");
      X.answer("20");
      X.close();
      tp(20.5, 41, 19);
      X.mountDragon();
      ok(
        !!X.mount && X.dragon.visible && d.expansion.flags.flew,
        "earned saddle and quest unlock flight",
      );
      const y = E.player.pos.y;
      for (let i = 0; i < 60; i++)
        X.drive(1 / 60, { moveX: 0, moveY: 0, jumpHeld: true });
      ok(E.player.pos.y > y + 5, "jump control raises the dragon");
      X.land();
      for (let i = 0; i < 180 && X.mount; i++)
        X.drive(1 / 60, { moveX: 0, moveY: 0, jumpHeld: false });
      ok(!X.mount, "Land descends and releases the player safely");
      tp(48.5, 69, 48.5);
      X.marker("sky");
      ok(d.expansion.flags.sky, "sky island grants discovery reward");
      X.action("portal");
      ok(
        E.player.pos.x > 100 && E.player.pos.y === 69,
        "earned portal reaches Moonflower Island",
      );
      X.marker("moon");
      ok(
        !!document.querySelector('[data-op="portal"]'),
        "Moonflower has a clear return portal",
      );
      X.action("portal");
      ok(
        E.player.pos.x < 80 && E.player.pos.y === 69,
        "sky portal works in both directions",
      );
      place("enchant_table", 24, 41, 20);
      s.coins = 500;
      s.gems = 20;
      X.gearPanel();
      X.action("enchant", { id: "frost" });
      X.answer("4");
      X.close();
      X.gearPanel();
      X.action("enchant", { id: "frost" });
      ok(
        d.expansion.enchant.frost && s.coins === 420,
        "learning and currency apply frost enchantment",
      );
      X.close();
      addItem(d, "armour_iron", 1);
      X.gearPanel();
      X.action("equip", { id: "armour_iron" });
      X.close();
      ok(
        d.expansion.gear.armour === "armour_iron" &&
          X.armour.children.length > 0,
        "equipping armour changes appearance",
      );
      E.player.invuln = 0;
      E.player.armourHits = 2;
      const hearts = E.player.hearts;
      E.player.hurt(1, E.player.pos.clone().add(new THREE.Vector3(1, 0, 0)));
      ok(E.player.hearts === hearts, "iron armour blocks every third hit");
      X.questPanel();
      X.action("camera");
      T.step(1);
      ok(
        game.camRig.firstPerson && !E.character.root.visible,
        "first-person camera hides own head",
      );
      X.questPanel();
      X.action("camera");
      T.step(1);
      ok(
        !game.camRig.firstPerson && E.character.root.visible,
        "third-person view restores character",
      );
      addItem(d, "bow", 1);
      addItem(d, "arrow", 12);
      assignSlot(d, "bow", 7);
      E.selectSlot(7);
      T.step(1);
      ok(document.querySelector("#tbtn-action").textContent.includes("SHOOT"), "bow button says Shoot rather than Gather");
      const arrows = amount(d, "arrow");
      X.shoot();
      ok(
        amount(d, "arrow") === arrows - 1 && X.arrowShots.length === 1,
        "bow launches a visible arrow and consumes one",
      );
      // 1.7 bow: hits anywhere on a monster's body, aim help in third person, arrows to pick back up.
      const V = (x, y, z) => new THREE.Vector3(x, y, z),
        label = () => document.querySelector("#tbtn-action-label").textContent,
        zombieBody = { body: { pos: V(0, 41, 0), hw: 0.3, h: 1.7 } },
        giantBody = { body: { pos: V(0, 41, 0), hw: 1, h: 5.2 } };
      ok(
        arrowHits(V(-5, 42.6, 0.5), V(5, 42.6, 0.5), zombieBody),
        "an arrow through a zombie's head hits (the old 1-block foot test missed it)",
      );
      ok(
        arrowHits(V(0.5, 42, -20), V(0.5, 42, 20), zombieBody),
        "a fast arrow crossing a thin zombie in a single step still hits",
      );
      ok(
        !arrowHits(V(-5, 42, 0.7), V(5, 42, 0.7), zombieBody) &&
          !arrowHits(V(-5, 43.1, 0), V(5, 43.1, 0), zombieBody) &&
          !arrowHits(V(-5, 42, 0), V(-1, 42, 0), zombieBody),
        "arrows passing beside, above or stopping short miss",
      );
      ok(
        arrowHits(V(-5, 45.5, 0), V(5, 45.5, 0), giantBody) &&
          arrowHits(V(1.3, 42, -5), V(1.3, 42, 5), giantBody) &&
          !arrowHits(V(-5, 45.5, 0), V(5, 45.5, 0), zombieBody),
        "the hit box follows each monster's size, up to a 5-block giant",
      );
      const animal = [...E.wildlife.animals.values()].find((a) => a.obj.visible);
      if (animal) {
        const health = animal.hp,
          at = animal.obj.position;
        X.launch(V(at.x, at.y + 0.8, at.z - 1.5), V(0, 0, 1));
        T.step(4);
        ok(animal.hp === health, "arrows fly past animals without hurting them");
      }
      X.clearArrows();
      // A real zombie waits in a clear lane at the west edge of town (monsters hold still while Sai is in town).
      tp(-28.5, 41, -23.5);
      T.step(3);
      const zombie = [...E.enemies.values()].find(
          (m) => m.kind === "zombie" && !m.dead && !m.dying,
        ),
        zombieHome = zombie.home.clone();
      zombie.home.set(-28.5, 41, -15.5);
      zombie.hp = 50;
      game.camRig.yaw = 0;
      T.step(2);
      ok(
        X.aim === zombie &&
          label() === "SHOOT\u00a0🎯" &&
          X.aimRing.visible &&
          Math.abs(X.aimRing.position.x + 28.5) < 0.01 &&
          Math.abs(X.aimRing.position.z + 15.5) < 0.01 &&
          Math.abs(X.aimRing.position.y - 41.06) < 0.05,
        "a zombie ahead gets a ring at its feet and the button says SHOOT 🎯",
      );
      game.camRig.yaw = Math.PI;
      T.step(1);
      ok(
        !X.aim && label() === "SHOOT" && !X.aimRing.visible,
        "looking away from the zombie shows plain SHOOT and no ring",
      );
      X.launch(V(-28, 42.6, -21.5), V(0, 0, 1));
      T.step(20);
      ok(
        zombie.hp === 50 - BOW.damage && !X.arrowShots.length && !X.arrowDrops.length,
        "a shot through the zombie's head, 1 block above the old foot test, hits for 3",
      );
      zombie.home.set(-26.5, 41, -15.5);
      game.camRig.yaw = 0;
      T.step(1);
      const quiver = amount(d, "arrow");
      X.shotAt = -10;
      X.shoot();
      T.step(30);
      ok(
        X.aim === zombie &&
          zombie.hp === 50 - 2 * BOW.damage &&
          amount(d, "arrow") === quiver - 1 &&
          !X.arrowDrops.length,
        "aim help turns a third-person shot towards a zombie 2 blocks off the camera's line",
      );
      // Eye view keeps the crosshair ray; a miss sticks where it lands and comes back when walked over.
      game.camRig.setDistance(0);
      game.camRig.pitch = 0.6;
      T.step(2);
      const inBag = amount(d, "arrow");
      X.shotAt = -10;
      X.shoot();
      T.step(15);
      const stuck = X.arrowDrops[0]?.obj.position;
      ok(
        game.camRig.firstPerson &&
          !X.aim &&
          amount(d, "arrow") === inBag - 1 &&
          X.arrowDrops.length === 1 &&
          !!stuck &&
          Math.abs(stuck.y - 41.15) < 0.15 &&
          stuck.z > E.player.pos.z + 1.5,
        "a missed eye-view arrow sticks in the ground where it lands",
      );
      T.step(20);
      T.step(45, { moveY: 1 });
      ok(
        amount(d, "arrow") === inBag && !X.arrowDrops.length,
        "walking over a dropped arrow puts it back in the bag",
      );
      game.camRig.setDistance(7.5);
      game.camRig.pitch = 0.36;
      T.step(2);
      for (let i = 0; i < BOW.keep + 2; i++)
        X.launch(
          V(-30.5 + (i % 4) * 0.7, 44, -13.5 + Math.floor(i / 4) * 0.7),
          V(0, -1, 0),
        );
      T.step(15);
      ok(X.arrowDrops.length === BOW.keep, "at most 12 dropped arrows wait in the world");
      d.worldTime += BOW.lasts + 1;
      T.step(1);
      ok(!X.arrowDrops.length, "dropped arrows fade after 60 seconds of play");
      X.launch(V(-30.5, 44, -13.5), V(0, -1, 0));
      T.step(15);
      const lying = X.arrowDrops.length;
      zombie.home.copy(zombieHome);
      zombie.hp = zombie.maxHp;
      const saved = JSON.stringify(d.expansion);
      ensureExpansion(d);
      ok(
        d.expansion.flags.flew &&
          d.expansion.devices[key] &&
          d.expansion.animals.length === 4,
        "save validation retains quests devices and animals",
      );
      game.save.persist();
      await game.startExplore();
      ok(
        game.explore.data.expansion.flags.flew &&
          game.explore.data.expansion.devices[key],
        "re-entering Explore restores expansion",
      );
      ok(
        lying === 1 &&
          !X.arrowDrops.length &&
          !game.explore.expansion.arrowDrops.length &&
          !JSON.stringify(game.save.data).includes("arrowDrops"),
        "arrows left lying around are tidied away and never saved",
      );
    } catch (e) {
      report.errors.push(e.stack || String(e));
    } finally {
      game.showMenu();
      game.save.data = previous;
      game.save.persist();
    }
    return report;
  };
  // 1.7 Mossy Giant. The first giant region in a spiral out from spawn (ring by ring).
  const firstGiantRegion = () => {
    for (let r = 1; r <= 12; r++)
      for (let rz = -r; rz <= r; rz++)
        for (let rx = -r; rx <= r; rx++)
          if (Math.max(Math.abs(rx), Math.abs(rz)) === r && giantRegion(rx, rz)) return { rx, rz };
    return null;
  };
  // Stand on the level trail strip `back` blocks west of that giant's spot, looking east at it (the strip behind
  // keeps the camera clear); returns the giant after it has turned to face the player.
  T.nearGiant = (back = 6) => {
    const E = game.explore,
      { rx, rz } = firstGiantRegion(),
      x = rx * REGION + GIANT_SPOT.x - back,
      z = rz * REGION + GIANT_SPOT.z - 1;
    game.resume();
    E.player.spawn(new THREE.Vector3(x, 41, z), Math.PI / 2);
    E.stream.update(x, z, true);
    E.player.invuln = 0;
    // A low camera keeps the giant's head and tag under the top HUD; on wide screens it stands right of centre.
    game.camRig.pitch = 0.06;
    game.camRig.snap(E.player.pos, Math.PI / 2 + (game.camera.aspect > 1 ? 0.3 : 0));
    T.step(30);
    return E.enemies.get(regionKey(rx, rz));
  };
  T.playGiant = async () => {
    const previous = JSON.parse(JSON.stringify(game.save.data));
    const report = { name: "Mossy Giant", pass: [], fail: [], errors: [] },
      ok = (x, m) => (x ? report.pass : report.fail).push(m);
    const random = Math.random,
      toast = game.ui.toast,
      toasts = [];
    game.testHold = true;
    game.showMenu();
    game.save.data.discovery = freshDiscovery();
    game.save.data.stats.giants = 0;
    game.giantMet = false;
    game.ui.toast = function (text, ...rest) {
      toasts.push(String(text));
      return toast.call(this, text, ...rest);
    };
    try {
      await game.startExplore();
      game.camRig.setDistance(7.5);
      const E = game.explore,
        X = E.expansion,
        d = E.data,
        s = game.save.data,
        pl = E.player;
      const { rx, rz } = firstGiantRegion();
      ok(Math.max(Math.abs(rx), Math.abs(rz)) >= 2, `a spiral search finds the nearest giant region (${rx},${rz}) beyond the first ring`);
      const giant = T.nearGiant(),
        home = new THREE.Vector3(rx * REGION + GIANT_SPOT.x, 41, rz * REGION + GIANT_SPOT.z),
        bar = () => document.querySelector("#explore-hud .giant-bar"),
        barWidth = () => bar()?.querySelector("i").style.width,
        beaten = () => toasts.filter((t) => t.startsWith("🗿 You beat the Mossy Giant!"));
      // Stand somewhere (fresh hearts, no armour luck), with the giant calm at home.
      const ready = (at, yaw = 0) => {
        game.resume();
        giant.body.pos.copy(home);
        giant.body.vel.set(0, 0, 0);
        giant.calm();
        giant.stun = 0;
        pl.spawn(at.clone(), yaw);
        E.stream.update(at.x, at.z, true);
        pl.invuln = 0;
        pl.armourHits = 0;
        pl.hearts = 5;
        d.hearts = 5;
      };
      ok(
        giant?.kind === "giant" && giant.hp === 10 && giant.maxHp === 10 && giant.body.hw === 1 && giant.body.h === 5.2,
        "a Mossy Giant replaces that region's monster, with 10 strength and a 2-wide, 5.2-tall body",
      );
      ok(giant.home.distanceTo(home) < 0.01, "it stands at the same spot of its region for every player");
      ok(giant.tag?.name === "MOSSY GIANT" && giant.tag.visible && giant.obj.visible, "a MOSSY GIANT tag floats over it");
      ok(!!bar() && !bar().hidden && barWidth() === "100%" && bar().textContent.includes("Mossy Giant"), "its strength bar shows while it is near");
      ok(toasts.filter((t) => t.includes("A Mossy Giant!")).length === 1, "the first meeting explains how to beat it");

      // Four aimed arrows (3 damage each) beat it, and the loot is paid exactly.
      addItem(d, "bow", 1);
      addItem(d, "arrow", 12);
      assignSlot(d, "bow", 7);
      E.selectSlot(7);
      ready(new THREE.Vector3(home.x - 11, 41, home.z - 2));
      const aim = () => {
          const q = giant.body.pos;
          game.camRig.yaw = Math.atan2(q.x - pl.pos.x, q.z - pl.pos.z);
        },
        before = { crystal: amount(d, "crystal"), iron: amount(d, "iron"), diamond: amount(d, "diamond"), coins: s.coins };
      aim();
      T.step(1);
      ok(X.aim === giant && X.aimRing.visible, "bow aim help rings the giant");
      for (let i = 0; i < 4; i++) {
        aim();
        T.step(1);
        if (i === 3) Math.random = () => 0.1; // this time the 40% diamond drops
        X.shotAt = -10;
        X.shoot();
        for (let n = 0; n < 60 && X.arrowShots.length; n++) T.step(1);
        Math.random = random;
        T.step(1);
        if (i === 0) ok(giant.hp === 10 - BOW.damage && barWidth() === "70%", "an arrow takes 3 strength and the bar shrinks to match");
      }
      ok(giant.hp <= 0 && !giant.obj.visible && d.defeated[giant.region] > d.worldTime, "four arrows beat the Mossy Giant");
      ok(
        amount(d, "crystal") === before.crystal + 3 &&
          amount(d, "iron") === before.iron + 2 &&
          s.coins === before.coins + 15 &&
          s.stats.giants === 1,
        "beating it pays 3 crystal, 2 iron and exactly 15 coins, and counts the win",
      );
      ok(
        amount(d, "diamond") === before.diamond + 1 &&
          beaten()[0] === "🗿 You beat the Mossy Giant! +3 crystal · +2 iron · +15 coins · +1 diamond ✨",
        "a lucky roll adds a diamond, and the cheer says so",
      );
      ok(bar().hidden, "the bar hides once it is beaten");

      d.worldTime += 151;
      T.step(1);
      ok(giant.obj.visible && giant.hp === 10 && !giant.dying && giant.body.pos.distanceTo(home) < 0.5, "after a 150-second rest it is back at full strength");

      // Standing beside it on the ground: arms up for the windup, then a stomp costs exactly one heart.
      const beside = new THREE.Vector3(home.x + 2.4, 41, home.z);
      ready(beside);
      let stomps = giant.stomps,
        frames = 0,
        raised = false;
      while (giant.stomps === stomps && frames < 90) {
        T.step(1);
        frames++;
        raised ||= giant.golem.armL.rotation.x < -1.5;
      }
      ok(
        giant.stomps === stomps + 1 && raised && Math.abs(frames / 60 - GIANT.windup) < 0.05,
        `it raises its arms for ${GIANT.windup} s, then stomps (${frames} frames)`,
      );
      ok(pl.hearts === 4 && d.hearts === 4, "a stomp costs a player on the ground exactly one heart");
      // The same stomp while the player is in the air misses.
      ready(beside);
      stomps = giant.stomps;
      for (let n = 0; n < 60 && giant.windup < 0.25; n++) T.step(1);
      T.step(1, { jumpHeld: true }, ["jump"]);
      let airborne = null;
      for (let n = 0; n < 60 && giant.stomps === stomps; n++) {
        const grounded = pl.onGround;
        T.step(1, { jumpHeld: true });
        if (giant.stomps !== stomps) airborne = !grounded;
      }
      ok(airborne === true && pl.hearts === 5, "jumping over the shockwave dodges it");

      // Landing on its head bounces the player off: it is too big to squash.
      ready(new THREE.Vector3(home.x, home.y + 6.5, home.z));
      pl.vel.set(0, -4, 0);
      let bounced = false;
      for (let n = 0; n < 60 && !bounced; n++) {
        T.step(1);
        bounced = pl.vel.y > 5;
      }
      ok(bounced && giant.hp === 10 && !giant.dying, "jumping onto it bounces the player off and leaves its strength alone");
      ok([...document.querySelectorAll(".float-text")].some((e) => e.textContent === "Too big to squash!"), "it is too big to squash");
      T.step(8);
      ok(pl.hearts === 5, "bouncing off its head costs no heart");

      // Sword hits take the sword's power and never knock it back.
      addItem(d, "sword_iron", 1);
      assignSlot(d, "sword_iron", 6);
      E.selectSlot(6);
      ready(new THREE.Vector3(home.x - 2.2, 41, home.z), Math.PI / 2);
      giant.stompCD = 99;
      T.step(1);
      ok(document.querySelector(".explore-prompt").textContent.includes("🗿 Mossy Giant · 10 strength"), "the prompt names the Mossy Giant and its strength");
      const power = ITEMS.sword_iron.power,
        at = giant.body.pos.clone();
      T.step(1, {}, ["e"]);
      for (let n = 0; n < 30 && giant.hp === 10; n++) T.step(1);
      ok(giant.hp === 10 - power, `a sword hit takes the sword's power (${power})`);
      ok(
        Math.hypot(giant.body.vel.x, giant.body.vel.z) < 2 && giant.body.pos.x <= at.x + 0.02,
        "a sword hit never knocks the giant back",
      );
      Math.random = () => 0.9; // no diamond this time
      for (let n = 0; n < 10 && giant.hp > 0; n++) {
        E.strikeTarget = giant;
        E.swordHit(pl);
      }
      Math.random = random;
      T.step(1);
      ok(
        s.stats.giants === 2 && amount(d, "diamond") === before.diamond + 1 && beaten()[1] === "🗿 You beat the Mossy Giant! +3 crystal · +2 iron · +15 coins",
        "swords beat it too; without the lucky roll there is no diamond",
      );

      // It follows the player along the trail, but is back home once they walk more than 32 blocks away.
      d.worldTime += 151;
      ready(new THREE.Vector3(home.x - 8, 41, home.z - 2));
      T.step(60);
      ok(giant.body.pos.distanceTo(home) > 0.8, "it follows a nearby player");
      game.camRig.yaw = -Math.PI / 2;
      let walked = 0;
      for (; walked < 600 && pl.pos.distanceTo(giant.body.pos) <= 31; walked++) T.step(1, { moveY: 1 });
      ok(walked > 60 && giant.body.pos.distanceTo(home) > 0.5, "beyond its 16-block reach it walks home rather than jumping there");
      for (let n = 0; n < 120 && giant.body.pos.distanceTo(home) > 0.01; n++) T.step(1, { moveY: 1 });
      ok(
        pl.pos.distanceTo(home) > 32 && giant.body.pos.distanceTo(home) < 0.01 && bar().hidden,
        "walking more than 32 blocks away sends it home and hides the bar",
      );
      ok(toasts.filter((t) => t.includes("A Mossy Giant!")).length === 1, "the meeting tip shows once per game session");
    } catch (e) {
      report.errors.push(e.stack || String(e));
    } finally {
      Math.random = random;
      game.ui.toast = toast;
      game.camRig.pitch = 0.36;
      game.showMenu();
      game.save.data = previous;
      game.save.persist();
    }
    return report;
  };
  T.playExpedition = async (kind) => {
    const report = {
        name: "Discovery adventure " + kind,
        pass: [],
        fail: [],
        errors: [],
      },
      ok = (v, m) => (v ? report.pass : report.fail).push(m);
    try {
      const oldPet=game.save.data.equip.pet;
      game.save.data.equip.pet="dog";
      await T.startLevel("1-" + kind);
      game.save.data.equip.pet=oldPet;
      const L = game.level,
        e = L.expedition;
      ok(L.state === "play" && !!e, "chapter boots with its own course");
      ok(!L.world.get(0, 40, 19), "bridge really starts missing");
      for (let i = 0; i < 3; i++) {
        T.tp(0.5, 41, e.clues[i].z);
        e.ask(i);
        ok(
          L.state === "quiz" && L.player.frozen,
          "clue " + i + " pauses action",
        );
        e.answer("wrong");
        ok(!e.done.has(i), "clue " + i + " supports retry");
        e.answer(e.clues[i].answer);
        ok(
          e.done.has(i) && L.state === "play",
          "clue " + i + " changes the world",
        );
        if (i === 0) {
          ok(
            L.world.get(0, 40, 19) === B.PLANKS,
            "correct area answer constructs a real bridge",
          );
          T.tp(0.5, 41, 16);
          T.step(85, { moveY: 1 });
          ok(
            L.player.pos.z > 22 && L.player.pos.y >= 41,
            "walking crosses the rebuilt bridge",
          );
          if (kind !== "temple") {
            T.tp(0.5, 41, 28);
            e.board();
            ok(!L.pet.obj.visible,"following pet stays clear of the mounted rider");
            for (let n = 0; n < 430; n++) e.drive(1 / 60, { moveX: 0 });
            ok(
              !e.ride && L.player.pos.z === 63,
              "vehicle carries player safely across special section",
            );
            ok(L.pet.obj.visible,"pet returns after the ride");
          }
        }
      }
      ok(
        L.portal.active && L.portal.obj.visible,
        "all clues open a visible portal",
      );
      const a =
        game.save.data.discovery.expansion ||
        ensureExpansion(game.save.data.discovery);
      delete a.adventures[kind];
      e.award();
      ok(a.adventures[kind], "first finish awards Explore supplies");
      const items = JSON.stringify(game.save.data.discovery.inventory.items);
      e.award();
      ok(
        JSON.stringify(game.save.data.discovery.inventory.items) === items,
        "replay does not duplicate first-clear bundle",
      );
      L.enterPortal();
      ok(L.state === "win", "finished chapter enters celebration");
      game.showMenu();
    } catch (e) {
      report.errors.push(e.stack || String(e));
    }
    return report;
  };
}
